import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import ChatContextMenu from './ChatContextMenu_Component';

/**
 * Component props interface
 */
interface ProjectSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string | null;
  projectSummary: string | null;
  projectId?: string | null;
  onRename?: () => void;
  onDelete?: () => void;
}

type MenuPosition = { top: number; left: number };

interface ProjectOptionsDropdownProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  menuPosition: MenuPosition;
  setMenuPosition: (position: MenuPosition) => void;
  onClose: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

function ProjectOptionsDropdown({
  isMenuOpen,
  setIsMenuOpen,
  menuPosition,
  setMenuPosition,
  onClose,
  onRename,
  onDelete,
}: ProjectOptionsDropdownProps) {
  return (
    <div className="relative">
      <button
        onClick={(e) => {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setMenuPosition({ top: rect.bottom + 4, left: rect.left });
          setIsMenuOpen(!isMenuOpen);
        }}
        className="p-1 rounded-sm hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
        aria-label="Project options"
      >
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isMenuOpen && (
        <ChatContextMenu
          isOpen={true}
          onClose={() => setIsMenuOpen(false)}
          position={menuPosition}
          onRename={onRename ? () => {
            setIsMenuOpen(false);
            onClose();
            onRename();
          } : undefined}
          onDelete={onDelete ? () => {
            setIsMenuOpen(false);
            onClose();
            onDelete();
          } : undefined}
        />
      )}
    </div>
  );
}

function ProjectSummaryBody({ projectSummary }: { projectSummary: string | null }) {
  return (
    <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-80px)]">
      {projectSummary ? (
        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
          {projectSummary}
        </p>
      ) : (
        <p className="text-gray-500 italic">
          No summary available yet. The project summary will appear here after you have a conversation.
        </p>
      )}
    </div>
  );
}

/**
 * Modal component to display project summary when user clicks on project name.
 * Shows project name as title and summary as body content.
 */
export default function ProjectSummaryModal({ isOpen, onClose, projectName, projectSummary, projectId, onRename, onDelete }: ProjectSummaryModalProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0 });

  // Reset menu state when modal closes or active project changes
  useEffect(() => {
    setIsMenuOpen(false);
    setMenuPosition({ top: 0, left: 0 });
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                {projectName || 'Project Summary'}
              </h2>
              {projectId && (
                <ProjectOptionsDropdown
                  isMenuOpen={isMenuOpen}
                  setIsMenuOpen={setIsMenuOpen}
                  menuPosition={menuPosition}
                  setMenuPosition={setMenuPosition}
                  onClose={onClose}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <ProjectSummaryBody projectSummary={projectSummary} />
        </div>
      </div>
    </>
  );
}
